import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, MapPin, X, Loader2, ChevronLeft, ChevronRight, Coffee } from 'lucide-react';
import { useCourtBooking, CourtSlot } from '@/hooks/useCourtBooking';
import { useLanguage } from '@/contexts/LanguageContext';

interface ParticipantCourtBookingProps {
  eventId: string;
  selectedWeek: number;
  matchDuration?: number;
  weekDateLabel?: string;
  totalWeeks?: number;
  weekLabels?: string[];
  restWeeks?: number[];
  weekStartDates?: Date[];
  onWeekChange?: (week: number) => void;
}

const allDaysKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const dayNormalizeMap: Record<string, typeof allDaysKeys[number]> = {
  monday: 'mon', tuesday: 'tue', wednesday: 'wed',
  thursday: 'thu', friday: 'fri', saturday: 'sat', sunday: 'sun',
  lunes: 'mon', martes: 'tue', miércoles: 'wed', miercoles: 'wed',
  jueves: 'thu', viernes: 'fri', sábado: 'sat', sabado: 'sat', domingo: 'sun',
};

const normalizeDayKey = (day: string): string => {
  return dayNormalizeMap[day.toLowerCase()] || day.toLowerCase();
};

const formatTime = (time: string) => time.substring(0, 5);

const ParticipantCourtBooking: React.FC<ParticipantCourtBookingProps> = ({
  eventId,
  selectedWeek,
  matchDuration = 90,
  weekDateLabel,
  totalWeeks,
  weekLabels,
  restWeeks = [],
  weekStartDates,
  onWeekChange,
}) => {
  const { t, language } = useLanguage();

  const translateCourtName = (name: string): string => {
    if (language === 'en') return name.replace(/^Cancha\b/i, 'Court');
    return name;
  };

  const adjustedTotalWeeks = (totalWeeks || 0) + restWeeks.length;

  const didMount = useRef(false);
  const [internalWeek, setInternalWeek] = useState(selectedWeek);

  // Always sync from parent prop
  useEffect(() => {
    setInternalWeek(selectedWeek);
  }, [selectedWeek]);

  // Only notify parent on user-driven changes (not initial mount)
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    onWeekChange?.(internalWeek);
  }, [internalWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCurrentRestWeek = restWeeks.includes(internalWeek);
  const maxWeek = adjustedTotalWeeks > 0 ? adjustedTotalWeeks : (totalWeeks || internalWeek);
  const currentWeekLabel = weekLabels && weekLabels[internalWeek - 1] ? weekLabels[internalWeek - 1] : weekDateLabel;
  const currentWeekStartDate = weekStartDates?.[internalWeek - 1] || undefined;

  const {
    slots,
    userBooking,
    hasBookingThisWeek,
    isLoading,
    isBooking,
    isCancelling,
    bookCourt,
    cancelBooking,
    hasRegistration,
  } = useCourtBooking(eventId, internalWeek, matchDuration, currentWeekStartDate);

  const daysWithSlots = useMemo(() => {
    const set = new Set<string>();
    slots.forEach(s => set.add(normalizeDayKey(s.dayOfWeek)));
    return set;
  }, [slots]);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  useEffect(() => {
    const firstAvailable = allDaysKeys.find(d => daysWithSlots.has(d));
    setSelectedDay(firstAvailable || null);
  }, [daysWithSlots]);

  const hasNoSlots = slots.length === 0;

  const filteredSlots = useMemo(() => {
    return selectedDay
      ? slots.filter(s => normalizeDayKey(s.dayOfWeek) === selectedDay)
      : slots;
  }, [slots, selectedDay]);

  const groupedByTime = useMemo(() => {
    const groups = new Map<string, CourtSlot[]>();
    filteredSlots.forEach(slot => {
      const key = `${slot.startTime}-${slot.endTime}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(slot);
    });
    return Array.from(groups.entries())
      .map(([, groupSlots]) => {
        const uniqueCourts = new Map<string, CourtSlot>();
        groupSlots.forEach(slot => {
          const existing = uniqueCourts.get(slot.courtName);
          if (!existing || slot.isBooked) {
            uniqueCourts.set(slot.courtName, slot);
          }
        });
        return {
          startTime: groupSlots[0].startTime,
          endTime: groupSlots[0].endTime,
          slots: Array.from(uniqueCourts.values()).sort((a, b) => a.courtNumber - b.courtNumber),
        };
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [filteredSlots]);

  const hasAnySlots = filteredSlots.length > 0;
  const hasAvailableSlots = filteredSlots.some(s => !s.isBooked);

  const getDayNameFromDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay();
    const dayKeys: (typeof allDaysKeys[number])[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const fullDayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`league.days.${fullDayKeys[dayIndex]}`);
  };

  if (isLoading) {
    return (
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
        <CardContent className="p-6 flex items-center justify-center gap-2 text-blue-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">{t('league.loadingAvailability')}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 shadow-md">
      <CardContent className="p-4 md:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-green-600" />
            <h3 className="font-bold text-green-900 text-base md:text-lg">
              {t('league.bookCourt')}
            </h3>
          </div>
          <Badge variant="outline" className="border-green-300 text-green-700 text-xs">
            {t('league.oneBookingPerWeek')}
          </Badge>
        </div>

        {/* Week navigator */}
        {maxWeek > 1 ? (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-700 hover:bg-green-100"
              onClick={() => setInternalWeek(w => Math.max(1, w - 1))}
              disabled={internalWeek <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-center min-w-[140px]">
              <p className="font-semibold text-green-900 text-sm">
                {t('league.week')} {internalWeek}
                {isCurrentRestWeek && <span className="text-amber-600 ml-1">· {t('league.rest')}</span>}
              </p>
              {currentWeekLabel && (
                <p className="text-xs text-green-600">{currentWeekLabel}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-700 hover:bg-green-100"
              onClick={() => setInternalWeek(w => Math.min(maxWeek, w + 1))}
              disabled={internalWeek >= maxWeek}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            {currentWeekLabel && (
              <p className="text-sm text-green-700">{t('league.week')} {internalWeek} · {currentWeekLabel}</p>
            )}
          </>
        )}

        {/* Rest week banner */}
        {isCurrentRestWeek && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center space-y-1">
            <div className="flex items-center justify-center gap-2 text-amber-700">
              <Coffee className="h-5 w-5" />
              <span className="font-semibold text-sm">{t('league.restWeekLabel')}</span>
            </div>
            <p className="text-xs text-amber-600">{t('league.noMatchesThisWeek')}</p>
          </div>
        )}

        {/* Day selector */}
        {!isCurrentRestWeek && (
        <div className="flex items-center justify-center gap-2">
          {allDaysKeys.map((key) => {
            const hasSlotsForDay = !hasNoSlots && daysWithSlots.has(key);
            const isSelected = selectedDay === key;
            return (
              <button
                key={key}
                disabled={!hasSlotsForDay}
                onClick={() => setSelectedDay(key)}
                className={`w-9 h-9 rounded-full text-xs font-semibold transition-colors
                  ${isSelected && hasSlotsForDay
                    ? 'bg-green-600 text-white shadow-md'
                    : hasSlotsForDay
                      ? 'border border-gray-300 text-gray-700 hover:border-green-400 hover:text-green-700 bg-white'
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
              >
                {t(`league.dayAbbr.${key}`)}
              </button>
            );
          })}
        </div>
        )}

        {/* No slots message */}
        {!isCurrentRestWeek && hasNoSlots && !isLoading && (
          <p className="text-sm text-gray-400 text-center py-3">
            {t('league.noSlotsAvailable')}
          </p>
        )}

        {/* User's current booking */}
        {!isCurrentRestWeek && userBooking && (
          <div className="bg-green-100 border border-green-300 rounded-lg p-3 md:p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-green-600 text-white text-xs">{t('league.yourBooking')}</Badge>
                </div>
                <div className="flex items-center gap-3 text-sm text-green-800">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {translateCourtName(userBooking.court_name)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {getDayNameFromDate(userBooking.booking_date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatTime(userBooking.start_time)} - {formatTime(userBooking.end_time)}
                  </span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => cancelBooking(userBooking.id)}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <X className="h-3 w-3 mr-1" />
                )}
                {t('league.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* No available slots message */}
        {!isCurrentRestWeek && !hasBookingThisWeek && hasAnySlots && !hasAvailableSlots && (
          <p className="text-sm text-gray-500 text-center py-2">
            {t('league.allSlotsBooked')}
          </p>
        )}

        {/* Grouped slots by time */}
        {!isCurrentRestWeek && groupedByTime.length > 0 && (
          <div className="space-y-3">
            {!hasBookingThisWeek && hasAvailableSlots && (
              <p className="text-xs text-green-700 font-medium">{t('league.availableSlots')}</p>
            )}
            {groupedByTime.map(({ startTime, endTime, slots: groupSlots }) => {
              const relevantSlots = groupSlots.filter(s => !s.bookedByCurrentUser);
              if (relevantSlots.length === 0) return null;

              return (
                <div
                  key={`${startTime}-${endTime}`}
                  className="bg-white rounded-lg border border-green-200 p-3 space-y-2"
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium text-green-800">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{formatTime(startTime)} - {formatTime(endTime)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {relevantSlots.map((slot, idx) => (
                      <div
                        key={`${slot.courtName}-${idx}`}
                        className={`rounded-lg border p-2.5 text-center transition-all ${
                          slot.isBooked
                            ? 'bg-gray-50 border-gray-200 opacity-50'
                            : 'bg-green-50/50 border-green-200 hover:shadow-sm'
                        }`}
                      >
                        <p className={`text-xs font-semibold mb-1.5 ${
                          slot.isBooked ? 'text-gray-400' : 'text-gray-800'
                        }`}>
                          {translateCourtName(slot.courtName)}
                        </p>
                        {slot.isBooked ? (
                          <Badge variant="secondary" className="text-[10px] px-2 py-0.5">
                            {t('league.booked')}
                          </Badge>
                        ) : hasBookingThisWeek ? (
                          <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-gray-400 border-gray-200">
                            {t('league.available')}
                          </Badge>
                        ) : !hasRegistration ? (
                          <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-gray-400 border-gray-200">
                            {t('league.register')}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-[10px] h-6 px-3 w-full"
                            onClick={() => bookCourt(slot)}
                            disabled={isBooking}
                          >
                            {isBooking ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              t('league.book')
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ParticipantCourtBooking;
